import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EventId, PlanEvent, Task, TaskId } from "../../../../shared/types";
import {
  CATEGORY_DOT_COLORS,
} from "../../../../shared/types";
import { getDayName, getDayNumber } from "../../../../shared/types/date-helpers";
import { useSwipe } from "../../../../shared/hooks/use-swipe";
import { HOURS, HOUR_HEIGHT_PX } from "../../utils/time-constants";
import { getNowIndicatorOffset } from "../../utils/time-positioning";
import { DayColumn } from "../week-grid/DayColumn";
import { ContextMenu } from "../../../../ui/atoms/ContextMenu";
import { setClipboardEvent } from "../../stores/clipboard-store";

interface MobileDayViewProps {
  readonly date: string;
  readonly today: string;
  readonly now: Date;
  readonly events: readonly PlanEvent[];
  readonly tasks: readonly Task[];
  readonly onEventClick: (event: PlanEvent) => void;
  readonly onEventLongPress?: (event: PlanEvent, anchorRect: DOMRect) => void;
  readonly onDeleteEvent?: (id: EventId) => void;
  readonly onSlotClick?: (startTime: string, endTime: string) => void;
  readonly onSwipeLeft: () => void;
  readonly onSwipeRight: () => void;
  readonly onCycleTaskStatus?: (id: TaskId) => void;
  readonly onTaskClick?: (task: Task) => void;
  readonly onAddTask?: () => void;
}

export function MobileDayView({
  date,
  today,
  now,
  events,
  tasks,
  onEventClick,
  onDeleteEvent,
  onSlotClick,
  onSwipeLeft,
  onSwipeRight,
  onCycleTaskStatus,
  onTaskClick,
  onAddTask,
}: MobileDayViewProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const { onTouchStart, onTouchMove, onTouchEnd } = useSwipe({
    onSwipeLeft,
    onSwipeRight,
  });

  const [ctxMenu, setCtxMenu] = useState<{
    event: PlanEvent;
    rect: DOMRect;
  } | null>(null);

  const handleLongPress = useCallback(
    (event: PlanEvent, anchorRect: DOMRect) => {
      setCtxMenu({ event, rect: anchorRect });
    },
    [],
  );

  const closeCtxMenu = useCallback(() => setCtxMenu(null), []);

  const SCROLL_THRESHOLD = 150;
  const AUTO_RETURN_DELAY = 30;
  const [showScrollToNow, setShowScrollToNow] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_RETURN_DELAY);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const ignoreScrollUntilRef = useRef(0);

  const clearCountdown = useCallback(() => {
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const scrollToNow = useCallback(() => {
    ignoreScrollUntilRef.current = Date.now() + 1000;
    setShowScrollToNow(false);
    setCountdown(AUTO_RETURN_DELAY);
    clearCountdown();
    const liveNow = new Date();
    const offset = getNowIndicatorOffset(liveNow);
    const el = scrollRef.current;
    if (offset !== null && el) {
      const target = Math.max(offset - 100, 0);
      const distance = Math.abs(el.scrollTop - target);
      if (distance > 800) {
        el.scrollTo({ top: target + (el.scrollTop > target ? 400 : -400), behavior: "instant" });
        requestAnimationFrame(() => el.scrollTo({ top: target, behavior: "smooth" }));
      } else {
        el.scrollTo({ top: target, behavior: "smooth" });
      }
    }
  }, [clearCountdown]);

  // Auto-scroll initial
  useEffect(() => {
    const offset = getNowIndicatorOffset(now);
    if (offset !== null && scrollRef.current) {
      scrollRef.current.scrollTop = Math.max(offset - 100, 0);
    }
    setShowScrollToNow(false);
    clearCountdown();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  // Détecte le scroll loin de "maintenant"
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const check = () => {
      if (Date.now() < ignoreScrollUntilRef.current) return;
      if (date !== today) { if (showScrollToNow) { setShowScrollToNow(false); clearCountdown(); } return; }
      const offset = getNowIndicatorOffset(now);
      if (offset === null) return;
      const idealTop = Math.max(offset - 100, 0);
      const distance = Math.abs(el.scrollTop - idealTop);
      if (distance > SCROLL_THRESHOLD && !showScrollToNow) {
        setShowScrollToNow(true);
        setCountdown(AUTO_RETURN_DELAY);
        clearCountdown();
        countdownRef.current = setInterval(() => {
          setCountdown((prev) => { if (prev <= 1) { scrollToNow(); return AUTO_RETURN_DELAY; } return prev - 1; });
        }, 1000);
      } else if (distance <= SCROLL_THRESHOLD && showScrollToNow) {
        setShowScrollToNow(false);
        clearCountdown();
      }
    };
    el.addEventListener("scroll", check, { passive: true });
    return () => el.removeEventListener("scroll", check);
  }, [date, today, now, showScrollToNow, clearCountdown, scrollToNow]);

  useEffect(() => clearCountdown, [clearCountdown]);

  const unlinkedTasks = tasks.filter((t) => !t.eventId && !t.archived);

  const linkedCountByEvent = useMemo(() => {
    const map = new Map<EventId, number>();
    for (const t of tasks) {
      if (t.eventId && !t.archived) {
        map.set(t.eventId, (map.get(t.eventId) ?? 0) + 1);
      }
    }
    return map;
  }, [tasks]);
  const [tasksCollapsed, setTasksCollapsed] = useState(true);
  const doneTasks = unlinkedTasks.filter((t) => t.status === "done").length;

  const allDayEvents = events.filter((e) => e.allDay);
  const timedEvents = events.filter((e) => !e.allDay);

  return (
    <div
      className="flex flex-col gap-2 h-full relative"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {allDayEvents.length > 0 && (
        <div className="px-2 flex flex-wrap gap-1.5 shrink-0 items-center">
          <span className="text-[10px] text-ink-faint uppercase tracking-wide mr-0.5">
            Journée
          </span>
          {allDayEvents.map((e) => (
            <button
              key={e.id}
              type="button"
              onClick={() => onEventClick(e)}
              className="flex items-center gap-1.5 bg-surface-alt rounded-full px-3 py-1.5 min-h-[32px] active:bg-surface-alt/70"
            >
              <span
                className={`w-2 h-2 rounded-full shrink-0 ${CATEGORY_DOT_COLORS[e.category]}`}
              />
              <span className="text-xs text-ink-muted truncate max-w-[140px]">
                {e.title}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Tâches du jour + bouton ajouter */}
      {(unlinkedTasks.length > 0 || onAddTask) && (
        <div className="px-2 py-1 shrink-0">
          <div className="flex items-center justify-between mb-1">
            <button
              type="button"
              onClick={() => setTasksCollapsed((v) => !v)}
              className="text-[10px] text-ink-faint uppercase tracking-wide flex items-center gap-1 active:text-ink-muted transition-colors"
            >
              <span className={`text-xs transition-transform ${tasksCollapsed ? "-rotate-90" : ""}`}>▾</span>
              <span className="font-medium">
                {getDayName(date)} {getDayNumber(date)}
              </span>
              {unlinkedTasks.length > 0 && (
                <span className="ml-1 text-ink-muted tabular-nums">
                  {doneTasks}/{unlinkedTasks.length}
                </span>
              )}
            </button>
            {onAddTask && (
              <button
                type="button"
                onClick={onAddTask}
                className={`text-xs font-semibold text-accent active:text-accent/70 transition-colors px-2 py-0.5 rounded-full ${tasksCollapsed ? "bg-accent/10" : ""}`}
              >
                + {tasksCollapsed ? "" : "Tâche"}
              </button>
            )}
          </div>
          {unlinkedTasks.length > 0 && !tasksCollapsed && (
            <div className="space-y-1">
              {unlinkedTasks.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onTaskClick ? onTaskClick(t) : onCycleTaskStatus?.(t.id)}
                  className="w-full flex items-center gap-2 bg-surface-alt rounded-lg px-2.5 py-1.5 active:bg-surface-alt/70 text-left"
                >
                  <span
                    className={`w-1.5 h-1.5 rounded-full shrink-0 ${CATEGORY_DOT_COLORS[t.category]}`}
                  />
                  <span className="flex-1 min-w-0 text-xs text-ink truncate">
                    {t.title}
                  </span>
                  {/* Indicateur de status compact - juste un point coloré */}
                  <span className={`w-2 h-2 rounded-full shrink-0 ${
                    t.status === "done" ? "bg-green-500" :
                    t.status === "doing" ? "bg-amber-500" :
                    t.priority === "urgent" ? "bg-red-400" :
                    "bg-gray-300"
                  }`} />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div ref={scrollRef} className="overflow-y-auto flex-1 -mx-2 px-2">
        <div className="grid" style={{ gridTemplateColumns: "48px 1fr" }}>
          {/* Hours labels */}
          <div className="relative">
            {HOURS.map((hour) => (
              <div
                key={hour}
                className="flex items-start justify-end pr-2"
                style={{ height: `${HOUR_HEIGHT_PX}px` }}
              >
                <span className="text-xs text-ink-faint -mt-1.5">
                  {String(hour).padStart(2, "0")}:00
                </span>
              </div>
            ))}
          </div>

          {/* Single day column */}
          <DayColumn
            date={date}
            today={today}
            now={now}
            events={timedEvents}
            linkedCountByEvent={linkedCountByEvent}
            onEventClick={onEventClick}
            onEventLongPress={handleLongPress}
            onSlotClick={onSlotClick}
          />
        </div>
      </div>

      {showScrollToNow && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20">
          <button
            type="button"
            onClick={scrollToNow}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-surface-alt border border-border/30 text-ink-muted text-xs font-medium rounded-full shadow-sm hover:text-accent hover:border-accent/30 transition-colors"
          >
            Maintenant
            <span className="text-ink-faint tabular-nums">{countdown}s</span>
          </button>
        </div>
      )}

      {ctxMenu && (
        <ContextMenu
          anchorRect={ctxMenu.rect}
          onClose={closeCtxMenu}
          items={[
            {
              label: "Modifier",
              onClick: () => { onEventClick(ctxMenu.event); closeCtxMenu(); },
            },
            {
              label: "Copier",
              onClick: () => { setClipboardEvent(ctxMenu.event); closeCtxMenu(); },
            },
            { separator: true },
            {
              label: "Supprimer",
              danger: true,
              onClick: () => { onDeleteEvent?.(ctxMenu.event.id); closeCtxMenu(); },
            },
          ]}
        />
      )}
    </div>
  );
}
